export interface NetworkState {
  timestamp: number;
  perfTime: number;
  online: boolean;
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  visibilityState: DocumentVisibilityState;
  audioContextState?: AudioContextState;
  mediaStreamActive?: boolean;
  mediaTrackReadyStates?: string[];
}

const HISTORY_LIMIT = 100;

type BrowserConnection = {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
};

export class NetworkMonitor {
  private history: NetworkState[] = [];
  private cleanupFns: Array<() => void> = [];

  constructor() {
    if (typeof window !== 'undefined') {
      this.setupListeners();
      this.captureState();
    }
  }

  private readConnection(): BrowserConnection {
    const nav = navigator as Navigator & {
      connection?: BrowserConnection;
      mozConnection?: BrowserConnection;
      webkitConnection?: BrowserConnection;
    };
    return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? {};
  }

  private buildState(
    audioContext?: AudioContext | null,
    mediaStream?: MediaStream | null
  ): NetworkState {
    const connection = this.readConnection();
    return {
      timestamp: Date.now(),
      perfTime: typeof performance !== 'undefined' ? performance.now() : Date.now(),
      online: navigator.onLine,
      effectiveType: connection.effectiveType,
      rtt: connection.rtt,
      downlink: connection.downlink,
      visibilityState: document.visibilityState,
      audioContextState: audioContext?.state,
      mediaStreamActive: mediaStream?.active,
      mediaTrackReadyStates: mediaStream?.getTracks().map((t) => t.readyState),
    };
  }

  private pushState(state: NetworkState): void {
    this.history.push(state);
    if (this.history.length > HISTORY_LIMIT) {
      this.history.splice(0, this.history.length - HISTORY_LIMIT);
    }
  }

  private setupListeners(): void {
    const capture = () => this.captureState();
    const listeners: Array<[EventTarget, string, EventListener]> = [
      [window, 'online', capture],
      [window, 'offline', capture],
      [window, 'pagehide', capture],
      [window, 'pageshow', capture],
      [document, 'visibilitychange', capture],
    ];

    listeners.forEach(([target, event, listener]) => {
      target.addEventListener(event, listener);
      this.cleanupFns.push(() => target.removeEventListener(event, listener));
    });
  }

  captureState(audioContext?: AudioContext | null, mediaStream?: MediaStream | null): NetworkState {
    const state = this.buildState(audioContext, mediaStream);
    this.pushState(state);
    return state;
  }

  getCurrentState(audioContext?: AudioContext | null, mediaStream?: MediaStream | null): NetworkState {
    return this.buildState(audioContext, mediaStream);
  }

  getStateHistory(): NetworkState[] {
    return [...this.history];
  }

  cleanup(): void {
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
  }
}
